import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { useOtpQueries } from './useOtpQueries';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { VoucherHeader } from './VoucherHeader';
import { useVoucherQueries } from './useVoucherQueries';
import { getAccessUuidForItem, setAccessUuidForItem } from './utils';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { VoucherGuestsEditTable } from './components/VoucherGuestsEditTable';
import { HideableTextEditor } from './components/HideableTextEditor';
import { VoucherVisibilityOptions } from './components/VoucherVisibilityOptions';
import { VoucherTransferEdit } from './components/VoucherTransferEdit';
import { VoucherGroundServiceEdit } from './components/VoucherGroundServiceEdit';
import { VoucherExperienceEdit } from './components/VoucherExperienceEdit';
import { VoucherHotelEdit } from './components/VoucherHotelEdit';
import { VoucherAddUploadModal } from './components/VoucherAddUploadModal';
import { ImageUpload } from 'services/ItineraryManagerApi/types';
import { UITextArea } from 'ui/UITextArea';
import { SlideToggle } from 'ui/SlideToggle';
import Editor from 'pureUi/Editor';
import { format } from 'date-fns';
import { useModal } from 'hooks/useModal';
import FluidButton from 'ui/FluidButton';
import { useVoucherEdit } from './hooks/useVoucherEdit';
import { isInternalUser as isInternalUserSelector } from 'store/modules/auth/selectors';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';

interface IVoucherEditPageRouteParams {
  voucherUuid: string;
}

export const VoucherEditPage = () => {
  const history = useHistory();
  const isInternalUser = useSelector(isInternalUserSelector);
  const { dynamicParameters } = useDynamicParameters();
  const match = useRouteMatch<IVoucherEditPageRouteParams>();
  const { voucherUuid } = match.params;
  const logoUploadModalOps = useModal();

  // Redirect to home page if user is not an internal user
  useEffect(() => {
    if (!isInternalUser) {
      history.push('/');
    }
  }, [isInternalUser, history]);

  const isOtpEnabled = dynamicParameters.ENABLE_OTP_TEMP !== false;
  const storedAccessUuid = getAccessUuidForItem(voucherUuid);

  // Always check OTP status when enabled
  const otpQueries = useOtpQueries(makeItineraryManagerApi(), voucherUuid, storedAccessUuid, isOtpEnabled);

  // Extract the response data
  const otpCheckData = otpQueries.checkIsOtpRequired.data?.data;
  const responseAccessUuid = otpCheckData?.accessUuid;
  const otpRequired = otpCheckData?.otpRequired;

  // Update stored access UUID if we got a new one from the response
  useEffect(() => {
    if (isOtpEnabled && responseAccessUuid && responseAccessUuid !== storedAccessUuid) {
      setAccessUuidForItem(voucherUuid, responseAccessUuid);
    }
  }, [isOtpEnabled, responseAccessUuid, storedAccessUuid, voucherUuid]);

  // Determine which access UUID to use: new one from response or stored one
  const currentAccessUuid = responseAccessUuid || storedAccessUuid;

  // Only fetch voucher data if we have an access UUID and OTP check is complete
  const shouldFetchVoucher =
    !isOtpEnabled || (otpQueries.checkIsOtpRequired.isSuccess && !otpRequired && currentAccessUuid);
  const voucherQueries = useVoucherQueries(
    makeItineraryManagerApi(),
    shouldFetchVoucher ? voucherUuid : undefined,
    currentAccessUuid
  );

  // Get voucher data
  const voucherData = voucherQueries.getVouchersV2View.data?.data;

  // Use custom hook for all edit logic
  const {
    voucherEditState,
    isEditedWithoutSaving,
    updateVoucherData,
    handleGuestVisibilityToggle,
    handleTransferVisibilityToggle,
    handleTransferDescriptionChange,
    handleTransferMoreTextChange,
    handleTransferAddUpload,
    handleTransferRemoveUpload,
    handleGroundServiceVisibilityToggle,
    handleGroundServiceDescriptionChange,
    handleGroundServiceMoreTextChange,
    handleGroundServiceAddUpload,
    handleGroundServiceRemoveUpload,
    handleExperienceVisibilityToggle,
    handleExperienceDescriptionChange,
    handleExperienceMoreTextChange,
    handleExperienceAddUpload,
    handleExperienceRemoveUpload,
    handleHotelPhoneVisibilityToggle,
    handleHotelEmailVisibilityToggle,
    handleAccommodationVisibilityToggle,
    handleAccommodationDescriptionChange,
    handleAccommodationMoreTextChange,
    handleMealPlanDescriptionChange,
    handleSupplementVisibilityToggle,
    handleSupplementTertiaryTextChange,
    handleAdditionalServiceVisibilityToggle,
    handleAdditionalServiceTertiaryTextChange,
    handleSupplementsMoreInfoChange,
    handleAdditionalServicesMoreInfoChange,
    handleRateFeaturesVisibilityToggle,
    handleAccommodationAddUpload,
    handleAccommodationRemoveUpload,
    handleMealPlanAddUpload,
    handleMealPlanRemoveUpload,
    handleLogoUpload,
    handleSave,
    handleCancel,
    isSaveLoading,
  } = useVoucherEdit({ voucherData, voucherUuid });

  // Handle loading state
  if (isOtpEnabled && otpQueries.checkIsOtpRequired.isLoading) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar label="Loading voucher data..." />
      </div>
    );
  }

  if (voucherQueries.getVouchersV2View.isLoading) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar label="Loading voucher data..." />
      </div>
    );
  }

  if (voucherQueries.getVouchersV2View.isError) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <ErrorBar message="Error loading voucher data. Please refresh the page and try again." />
      </div>
    );
  }

  // Redirect to OTP page if required
  if (isOtpEnabled && otpRequired) {
    history.push(`/otp/${voucherUuid}`);
    return null;
  }

  const voucherImage =
    'voucherImage' in voucherEditState ? voucherEditState.voucherImage : voucherData?.voucher?.voucherImage;

  const changeLogoButton = (
    <FluidButton
      type="secondary"
      onClick={async () => {
        const result = await logoUploadModalOps.requestConfirmation();
        handleLogoUpload(result);
      }}
    >
      {voucherImage ? 'Change Logo' : 'Add Logo'}
    </FluidButton>
  );

  return (
    <>
      <div className="mb-40px">
        <VoucherHeader
          upload={voucherImage as ImageUpload}
          changeLogoButton={changeLogoButton}
          mode={'edit'}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaveLoading={isSaveLoading}
          isEditedWithoutSaving={isEditedWithoutSaving}
          destinationFiles={[]}
        />

        <div className="mx-[20px] md:mx-[80px] mt-40px mb-20px">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-[24px] font-normal font-noe-display text-black">
                <span className="text-[36px]">Edit Voucher</span>
                {voucherData?.voucher?.countries && voucherData.voucher.countries.length > 0 && (
                  <span className="text-[26px] ml-2">
                    - {voucherData.voucher.countries.map(country => country.name).join(', ')}
                  </span>
                )}
              </span>
            </div>
            <div className="font-pt-sans text-right text-[13px] text-gray-100">
              {voucherData?.createdBy && (
                <div>
                  Created by: {voucherData.createdBy.firstName} {voucherData.createdBy.lastName},{' '}
                  {format(new Date(voucherData.createdBy.timestamp), 'dd MMM yyyy, HH:mm')}
                </div>
              )}
              {voucherData?.updatedBy && (
                <div>
                  Modified by: {voucherData.updatedBy.firstName} {voucherData.updatedBy.lastName},{' '}
                  {format(new Date(voucherData.updatedBy.timestamp), 'dd MMM yyyy, HH:mm')}
                </div>
              )}
            </div>
          </div>

          {/* Visibility options */}
          <VoucherVisibilityOptions
            peBookingNumberIsVisible={voucherEditState.peBookingNumber?.isVisible || false}
            travelPartnerReferenceIsVisible={voucherEditState.travelPartnerReference?.isVisible || false}
            destinationDetailsIsVisible={voucherEditState.destinationDetailsIsVisible}
            onPeBookingNumberVisibilityChange={isVisible => {
              updateVoucherData('peBookingNumber.isVisible', isVisible);
            }}
            onTravelPartnerReferenceVisibilityChange={isVisible => {
              updateVoucherData('travelPartnerReference.isVisible', isVisible);
            }}
            onDestinationDetailsVisibilityChange={isVisible => {
              updateVoucherData('destinationDetailsIsVisible', isVisible);
            }}
          />

          {/* Guest visibility section */}
          <div className="mb-8">
            <VoucherGuestsEditTable guests={voucherEditState.guests} onToggleVisibility={handleGuestVisibilityToggle} />
          </div>

          {/* Guests more info section */}
          <div className="flex items-center mb-10px">
            <div className="w-full pr-4 relative">
              <Editor
                handleEditorChange={content => {
                  updateVoucherData('guestsMoreInfo.text', content);
                }}
                mentionAbility={false}
                minHeight="125px"
                initVals={{
                  autoresize_bottom_margin: 0,
                  min_height: 125,
                }}
                text={voucherEditState.guestsMoreInfo.text}
                options={[]}
                placeHolder="Write here more info about guests..."
              />
            </div>
            <SlideToggle
              id={'guests-more-info'}
              checked={voucherEditState.guestsMoreInfo?.isVisible || false}
              onActivate={() => updateVoucherData('guestsMoreInfo.isVisible', true)}
              onDeactivate={() => updateVoucherData('guestsMoreInfo.isVisible', false)}
              labelClassName={!voucherEditState.guestsMoreInfo?.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
              buttonClassName={!voucherEditState.guestsMoreInfo?.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
            />
          </div>

          {/* Special Request section */}
          <span className="font-pt-sans my-0 mb-5px w-full text-black pb-2 text-13px font-normal block">
            Special Request
          </span>
          <div className="flex items-center mb-10px">
            <UITextArea
              className="w-full pr-4"
              value={voucherEditState.specialRequest.text}
              disabled={true}
              autoResize={true}
              minRows={1}
            />
            <SlideToggle
              id={'special-request'}
              checked={voucherEditState.specialRequest?.isVisible || false}
              onActivate={() => updateVoucherData('specialRequest.isVisible', true)}
              onDeactivate={() => updateVoucherData('specialRequest.isVisible', false)}
              labelClassName={!voucherEditState.specialRequest?.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
              buttonClassName={!voucherEditState.specialRequest?.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
            />
          </div>

          {/* Special Request more info section */}
          <HideableTextEditor
            label="Special_Request_moreInfo"
            isLabelVisible={false}
            text={voucherEditState.specialRequestMoreInfo?.text || ''}
            isVisible={voucherEditState.specialRequestMoreInfo.isVisible}
            onTextChange={content => {
              updateVoucherData('specialRequestMoreInfo.text', content);
            }}
            onVisibilityChange={isVisible => {
              updateVoucherData('specialRequestMoreInfo.isVisible', isVisible);
            }}
          />

          {/* Hotels */}
          <VoucherHotelEdit
            hotels={voucherEditState.hotels}
            voucherUuid={voucherUuid}
            onHotelPhoneVisibilityToggle={handleHotelPhoneVisibilityToggle}
            onHotelEmailVisibilityToggle={handleHotelEmailVisibilityToggle}
            onAccommodationVisibilityToggle={handleAccommodationVisibilityToggle}
            onAccommodationDescriptionChange={handleAccommodationDescriptionChange}
            onAccommodationMoreTextChange={handleAccommodationMoreTextChange}
            onMealPlanDescriptionChange={handleMealPlanDescriptionChange}
            onSupplementVisibilityToggle={handleSupplementVisibilityToggle}
            onSupplementTertiaryTextChange={handleSupplementTertiaryTextChange}
            onAdditionalServiceVisibilityToggle={handleAdditionalServiceVisibilityToggle}
            onAdditionalServiceTertiaryTextChange={handleAdditionalServiceTertiaryTextChange}
            onSupplementsMoreInfoChange={handleSupplementsMoreInfoChange}
            onAdditionalServicesMoreInfoChange={handleAdditionalServicesMoreInfoChange}
            onRateFeaturesVisibilityToggle={handleRateFeaturesVisibilityToggle}
            onAccommodationAddUpload={handleAccommodationAddUpload}
            onAccommodationRemoveUpload={handleAccommodationRemoveUpload}
            onMealPlanAddUpload={handleMealPlanAddUpload}
            onMealPlanRemoveUpload={handleMealPlanRemoveUpload}
          />

          {/* Transfers section */}
          <VoucherTransferEdit
            transfers={voucherEditState.transfers}
            voucherUuid={voucherUuid}
            onVisibilityToggle={handleTransferVisibilityToggle}
            onDescriptionChange={handleTransferDescriptionChange}
            onMoreTextChange={handleTransferMoreTextChange}
            onAddUpload={handleTransferAddUpload}
            onRemoveUpload={handleTransferRemoveUpload}
          />

          {/* Ground Services section */}
          <VoucherGroundServiceEdit
            groundServices={voucherEditState.groundServices}
            voucherUuid={voucherUuid}
            onVisibilityToggle={handleGroundServiceVisibilityToggle}
            onDescriptionChange={handleGroundServiceDescriptionChange}
            onMoreTextChange={handleGroundServiceMoreTextChange}
            onAddUpload={handleGroundServiceAddUpload}
            onRemoveUpload={handleGroundServiceRemoveUpload}
          />

          {/* Experiences section */}
          <VoucherExperienceEdit
            experiences={voucherEditState.experiences}
            voucherUuid={voucherUuid}
            onVisibilityToggle={handleExperienceVisibilityToggle}
            onDescriptionChange={handleExperienceDescriptionChange}
            onMoreTextChange={handleExperienceMoreTextChange}
            onAddUpload={handleExperienceAddUpload}
            onRemoveUpload={handleExperienceRemoveUpload}
          />

          {/* further information fields */}
          <div className="mb-8">
            <span className="font-pt-sans font-bold text-[17px] text-black mb-4 block">Further Information</span>

            {/* Extra Services */}
            <HideableTextEditor
              label="Extra Services"
              text={voucherEditState.extraServices.text}
              isVisible={voucherEditState.extraServices.isVisible}
              onTextChange={content => {
                updateVoucherData('extraServices.text', content);
              }}
              onVisibilityChange={isVisible => {
                updateVoucherData('extraServices.isVisible', isVisible);
              }}
              className="mb-0"
            />

            {/* Additional Information */}
            <HideableTextEditor
              label="Additional Information"
              text={voucherEditState.additionalInformation.text}
              isVisible={voucherEditState.additionalInformation.isVisible}
              onTextChange={content => {
                updateVoucherData('additionalInformation.text', content);
              }}
              onVisibilityChange={isVisible => {
                updateVoucherData('additionalInformation.isVisible', isVisible);
              }}
              className="mb-0"
            />

            {/* Membership Benefits */}
            <HideableTextEditor
              label="Membership Benefits"
              text={voucherEditState.membershipBenefits.text}
              isVisible={voucherEditState.membershipBenefits.isVisible}
              onTextChange={content => {
                updateVoucherData('membershipBenefits.text', content);
              }}
              onVisibilityChange={isVisible => {
                updateVoucherData('membershipBenefits.isVisible', isVisible);
              }}
              className="mb-0"
            />

            {/* Policies and Restrictions */}
            <div className="mb-6">
              <span className="font-pt-sans my-0 mb-5px w-full text-black pb-2 text-13px font-normal block">
                Policies and Restrictions
              </span>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <span className="block font-pt-sans bg-ivory border-solid border-1 border-gray-40 text-[15px] p-[10px] text-black">
                    <HtmlStringRenderer content={voucherEditState.policiesAndRestrictions.text} />
                  </span>
                </div>
                <SlideToggle
                  id="toggle-policies-and-restrictions"
                  checked={voucherEditState.policiesAndRestrictions.isVisible}
                  onActivate={() => updateVoucherData('policiesAndRestrictions.isVisible', true)}
                  onDeactivate={() => updateVoucherData('policiesAndRestrictions.isVisible', false)}
                  labelClassName={!voucherEditState.policiesAndRestrictions.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                  buttonClassName={!voucherEditState.policiesAndRestrictions.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                />
              </div>
            </div>

            {/* Things to Be Aware Of */}
            <HideableTextEditor
              label="Things to Be Aware Of"
              text={voucherEditState.thingsToBeAwareOf.text}
              isVisible={voucherEditState.thingsToBeAwareOf.isVisible}
              onTextChange={content => {
                updateVoucherData('thingsToBeAwareOf.text', content);
              }}
              onVisibilityChange={isVisible => {
                updateVoucherData('thingsToBeAwareOf.isVisible', isVisible);
              }}
              className="mb-0"
            />

            {/* Emergency Contact */}
            <span className="font-pt-sans my-0 mb-5px w-full text-black pb-2 text-13px font-normal block">
              Emergency Contact
            </span>
            <div className="mb-6">
              <Editor
                handleEditorChange={content => {
                  updateVoucherData('emergencyContact', content);
                }}
                text={voucherEditState.emergencyContact}
                options={[]}
                placeHolder="Write emergency contact information..."
                minHeight="125px"
                initVals={{
                  autoresize_bottom_margin: 0,
                  min_height: 125,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={logoUploadModalOps.isOpen}
        onClose={logoUploadModalOps.handleClose}
        uploadType="image"
        handleConfirm={logoUploadModalOps.handleConfirm}
        modalTitle="Change Logo"
        displayNameOverwrite="Voucher Logo"
        isForLogo={true}
        voucherData={voucherData?.voucher}
      />

      <LeaveWithoutSavingModal when={isEditedWithoutSaving} />
    </>
  );
};
